// A quick NPC (issue 1157): a given name, a family name or byname and a trait, each an ordinary table roll through the
// server (so the log, the history and the dice bag's Private choice are the tables' own). There are no built-in lists:
// each slot is a table the DM picks — the system's starter table by default, or any of the DM's own — and the choice is
// remembered in this browser, a convenience only (it may come back empty).
import type { TableSource } from '@dragons-table/vtt-shared';
import { rollTable, type Answer, type SiteTable } from './rolltables';

export type Slot = 'given' | 'family' | 'trait';
export const SLOTS: ReadonlyArray<{ key: Slot; label: string; starter: { slug: string; name: string } }> = [
  { key: 'given', label: 'Given name', starter: { slug: 'given-names', name: 'Given Names' } },
  { key: 'family', label: 'Family name', starter: { slug: 'family-names', name: 'Family Names and Bynames' } },
  { key: 'trait', label: 'Trait', starter: { slug: 'npc-traits', name: 'NPC Traits' } },
];
export type Choices = Partial<Record<Slot, TableSource>>;
const KEY = 'vtt.quickNpc.tables';

/** The DM's remembered picks, or none. */
export function rememberedChoices(): Choices {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, unknown>;
    const out: Choices = {};
    for (const { key } of SLOTS) {
      const v = raw[key] as { source?: unknown; slug?: unknown; id?: unknown } | undefined;
      if (v?.source === 'site' && typeof v.slug === 'string') out[key] = { source: 'site', slug: v.slug };
      else if (v?.source === 'mine' && typeof v.id === 'string') out[key] = { source: 'mine', id: v.id };
    }
    return out;
  } catch { return {}; }
}
export function rememberChoices(c: Choices): void {
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* a convenience: nothing lost but the memory */ }
}

/** The system's starter table for a slot among the site's tables, by its address or its name; null when it has none. */
export function starterFor(slot: Slot, site: SiteTable[]): TableSource | null {
  const want = SLOTS.find((s) => s.key === slot)!.starter;
  const hit = site.find((t) => t.slug === want.slug) ?? site.find((t) => t.name === want.name);
  return hit ? { source: 'site', slug: hit.slug } : null;
}

export interface QuickNpc { given: string; family: string; trait: string }
/** The NPC as the roster keeps it: the name, and the trait as its note. */
export const npcName = (n: QuickNpc): string => `${n.given} ${n.family}`.replace(/\s+/g, ' ').trim();
export const npcLine = (n: QuickNpc): string => `${npcName(n)} — ${n.trait}`;

/** The three rolls, in order, each the tables' own; the first refusal stops it and is said. */
export async function rollQuickNpc(campaignId: string, tables: Record<Slot, TableSource>, priv: boolean, roll: typeof rollTable = rollTable): Promise<Answer<QuickNpc>> {
  const got: string[] = [];
  for (const { key } of SLOTS) {
    const r = await roll(campaignId, tables[key], priv);
    if (r.kind !== 'ok') return r;
    got.push(r.value ?? '');
  }
  return { kind: 'ok', value: { given: got[0]!, family: got[1]!, trait: got[2]! } };
}
